import React, { useEffect, useMemo, useRef } from 'react';
import { ArrowLeftRight, Download, Plus, RotateCcw, Trash2, Undo2, Upload } from 'lucide-react';
import NumericInput from '../NumericInput';
import CorporateProjectCitationChip from './CorporateProjectCitationChip';

const YEARS = [2027, 2028, 2029, 2030, 2031];
const HORIZON_MONTHS = 60;
const SPEND_TOLERANCE = 0.005;
const newId = () => `line-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const sumValues = (values = []) => values.reduce((sum, value) => sum + toNumber(value), 0);
const formatMM = (value) => `$${toNumber(value).toFixed(2)}`;

export const buildProjectSpreadTotals = (template = {}) => {
  const byYear = (collection, field, filter = () => true) => YEARS.map((_, index) =>
    (template[collection] || [])
      .filter(filter)
      .reduce((sum, line) => sum + toNumber((line[field] || [])[index]), 0));
  const revenueByYear = byYear('revenueLines', 'byYear', (line) => line.marginal !== false);
  const opexByYear = byYear('opexLines', 'byYear', (line) => !line.existing);
  const capexSpendByYear = byYear('capexItems', 'spendByYear');
  return {
    revenueByYear,
    opexByYear,
    capexSpendByYear,
    revenueTotal: sumValues(revenueByYear),
    opexTotal: sumValues(opexByYear),
    capexSpendTotal: sumValues(capexSpendByYear),
    capexCostTotal: (template.capexItems || []).reduce((sum, line) => sum + toNumber(line.totalCostMM), 0),
    grandByYear: YEARS.map((_, index) => revenueByYear[index] + opexByYear[index] + capexSpendByYear[index])
  };
};

export const validateProjectSpreadClient = (template = {}, durationMonths) => {
  const errors = [];
  const duration = Number.isFinite(Number(durationMonths)) ? Number(durationMonths) : null;
  const addError = (collection, line, field, message) => errors.push({
    collection,
    lineId: line.id,
    field,
    message,
    lineDescription: line.description || line.id
  });
  ['revenueLines', 'opexLines'].forEach((collection) => {
    (template[collection] || []).forEach((line) => {
      (line.byYear || []).forEach((value, index) => {
        if (toNumber(value) < 0) {
          addError(collection, line, `byYear.${index}`, `FY${YEARS[index]} cannot be negative.`);
        }
      });
    });
  });
  (template.capexItems || []).forEach((line) => {
    if (toNumber(line.totalCostMM) < 0) {
      addError('capexItems', line, 'totalCostMM', 'Total cost cannot be negative.');
    }
    (line.spendByYear || []).forEach((value, index) => {
      if (toNumber(value) < 0) {
        addError('capexItems', line, `spendByYear.${index}`, `FY${YEARS[index]} spend cannot be negative.`);
      }
    });
    const spendTotal = sumValues(line.spendByYear || []);
    if (Math.abs(spendTotal - toNumber(line.totalCostMM)) > SPEND_TOLERANCE) {
      addError('capexItems', line, 'spendByYear', 'Spend by year must sum to total cost.');
    }
    const inService = Number(line.inServiceMonth);
    if (!Number.isFinite(inService) || inService < 1 || inService > HORIZON_MONTHS) {
      addError('capexItems', line, 'inServiceMonth', 'In-service month must be within the 60-month model horizon.');
    } else if (duration && inService > duration) {
      addError('capexItems', line, 'inServiceMonth', `In-service month must be within the stated project duration (${duration} months).`);
    }
  });
  return { status: errors.length ? 'blocked' : 'ready', errors, warnings: [] };
};

const PROPOSAL_STATUS = {
  accepted: ['Accepted', 'green'],
  edited: ['Edited', 'gold'],
  rejected: ['Rejected', 'red'],
  pending: ['Pending review', 'yellow'],
  manual: ['Manual', 'neutral']
};

const ProposalStatus = ({ line }) => {
  const [label, tone] = PROPOSAL_STATUS[line.proposalStatus] || [];
  if (!label) return null;
  return <span className={`forecast-health-chip ${tone} mini`}>{label}</span>;
};

const CapitalizationCell = ({
  clearCapOverride,
  collection,
  defaultLabel,
  editable,
  line,
  locked,
  overrideLabel,
  setCapOverride,
  toggleOverride,
  toggleTitle
}) => {
  const override = line.capOverride;

  return (
    <td>
      <span
        className={`forecast-health-chip ${override ? 'gold' : 'neutral'} mini`}
        title={override
          ? `by ${override.by || '—'} ${override.at ? new Date(override.at).toLocaleString() : ''}`.trim()
          : 'Derived default'}
      >
        {override ? overrideLabel : defaultLabel}
      </span>
      {editable && !locked && (
        override ? (
          <button
            type="button"
            className="hire-expand"
            title="Reset to derived default (clears override)"
            aria-label="Reset capitalization to the derived default"
            onClick={() => clearCapOverride(collection, line.id)}
          >
            <RotateCcw size={13} aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="hire-expand"
            title={toggleTitle}
            aria-label={toggleTitle}
            onClick={() => setCapOverride(collection, line.id, toggleOverride)}
          >
            <ArrowLeftRight size={13} aria-hidden="true" />
          </button>
        )
      )}
    </td>
  );
};

const CorporateProjectSpreadStage = ({
  activeCitation,
  addLine,
  bucketTermMonths,
  capexBuckets,
  canUndo,
  citationById,
  clearCapOverride,
  durationMonths,
  editable,
  focusedLineId,
  importPreview,
  isPdfSource,
  onApplyImport,
  onCancelImport,
  onExport,
  onImport,
  onOpenCitation,
  removeLine,
  spreadBusy,
  spreadValidation,
  setCapOverride,
  setSpreadTab,
  spreadTab,
  template,
  undoLastEdit,
  updateLine
}) => {
  const gridRef = useRef(null);
  const importFileRef = useRef(null);
  const validation = spreadValidation || validateProjectSpreadClient(template, durationMonths);
  const totals = useMemo(() => buildProjectSpreadTotals(template), [template]);
  const errorByCell = useMemo(() => {
    const index = {};
    (validation.errors || []).forEach((error) => {
      const keys = [
        `${error.collection}:${error.lineId}:${error.field}`,
        `${error.collection}:${error.lineId}:${String(error.field).split('.')[0]}`
      ];
      keys.forEach((key) => {
        index[key] = index[key] || error;
      });
    });
    return index;
  }, [validation.errors]);

  useEffect(() => {
    if (!focusedLineId) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`project-spread-line-${focusedLineId}`);
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({ block: 'center' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusedLineId, spreadTab]);

  const citationChip = (figureId) => (
    <CorporateProjectCitationChip
      activeCitation={activeCitation}
      citation={figureId ? citationById[figureId] : null}
      isPdfSource={isPdfSource}
      onOpen={onOpenCitation}
    />
  );

  const cellError = (collection, lineId, field) =>
    errorByCell[`${collection}:${lineId}:${field}`] || null;

  const errorMessage = (error) => error ? (
    <small
      className="forecast-home-error"
      role="alert"
      style={{ display: 'block', marginTop: 4, padding: '2px 4px' }}
    >
      {error.message}
    </small>
  ) : null;

  const inputClass = (error) => `op3-stepper${error ? ' project-spread-cell-error' : ''}`;
  const errorStyle = (error) => error ? { outline: '2px solid #d9534f', outlineOffset: 1 } : undefined;

  const moveGridFocus = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) return;
    if (event.target?.tagName !== 'INPUT') return;
    const currentCell = event.target.closest('[data-grid-cell]');
    const table = currentCell?.closest('[data-project-grid]');
    if (!currentCell || !table) return;
    const cells = Array.from(table.querySelectorAll('[data-grid-cell]'))
      .filter((cell) => cell.querySelector('input:not([disabled])'));
    const currentIndex = cells.indexOf(currentCell);
    if (currentIndex < 0) return;
    event.preventDefault();
    const row = Number(currentCell.dataset.gridRow);
    const col = Number(currentCell.dataset.gridCol);
    let targetCell = null;
    if (event.key === 'ArrowLeft') targetCell = cells[currentIndex - 1];
    if (event.key === 'ArrowRight' || event.key === 'Enter') targetCell = cells[currentIndex + 1];
    if (event.key === 'ArrowUp') {
      targetCell = cells.find((cell) => Number(cell.dataset.gridRow) === row - 1 && Number(cell.dataset.gridCol) === col)
        || cells[currentIndex - 1];
    }
    if (event.key === 'ArrowDown') {
      targetCell = cells.find((cell) => Number(cell.dataset.gridRow) === row + 1 && Number(cell.dataset.gridCol) === col)
        || cells[currentIndex + 1];
    }
    event.target.blur();
    window.setTimeout(() => targetCell?.querySelector('input:not([disabled])')?.focus(), 0);
  };

  const triggerImport = () => importFileRef.current?.click();

  const handleImportChange = (event) => {
    const file = event.target.files?.[0];
    if (file) onImport?.(file);
    event.target.value = '';
  };

  const TotalsFooter = ({ sectionLabel, sectionValues, sectionTotal, fixedSpan, includeCostTotal = false }) => (
    <tfoot>
      <tr style={{ position: 'sticky', bottom: 34, background: '#fff', boxShadow: '0 -1px 0 rgba(0,0,0,0.12)' }}>
        <td colSpan={fixedSpan}><strong>{sectionLabel}</strong></td>
        {includeCostTotal && <td className="forecast-mono"><strong>{formatMM(totals.capexCostTotal)}</strong></td>}
        {includeCostTotal && <td />}
        {sectionValues.map((value, index) => (
          <td key={YEARS[index]} className="forecast-mono"><strong>{formatMM(value)}</strong></td>
        ))}
        <td className="forecast-mono"><strong>{formatMM(sectionTotal)}</strong></td>
      </tr>
      <tr style={{ position: 'sticky', bottom: 0, background: '#f8f9fb', boxShadow: '0 -1px 0 rgba(0,0,0,0.16)' }}>
        <td colSpan={fixedSpan + (includeCostTotal ? 2 : 0)}><strong>Grand total by year</strong></td>
        {totals.grandByYear.map((value, index) => (
          <td key={YEARS[index]} className="forecast-mono"><strong>{formatMM(value)}</strong></td>
        ))}
        <td className="forecast-mono"><strong>{formatMM(sumValues(totals.grandByYear))}</strong></td>
      </tr>
    </tfoot>
  );

  return (
    <section ref={gridRef} className="forecast-panel workspace-stage-panel" id="project-stage-spread" tabIndex="-1">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">PRJ_02 ● Project template</span>
        <h2>Marginal revenue only · incremental expense only · capex bucketed into capitalization terms</h2>
      </div>

      <div className="workspace-action-row" style={{ marginBottom: 10 }}>
        <button
          type="button"
          className="forecast-secondary-button compact"
          disabled={spreadBusy === 'spread-export'}
          onClick={onExport}
        >
          <Download size={12} aria-hidden="true" /> {spreadBusy === 'spread-export' ? 'Exporting…' : 'Export XLSX'}
        </button>
        <button
          type="button"
          className="forecast-secondary-button compact"
          disabled={!editable || spreadBusy === 'spread-import-preview' || spreadBusy === 'spread-import-apply'}
          onClick={triggerImport}
        >
          <Upload size={12} aria-hidden="true" /> {spreadBusy === 'spread-import-preview' ? 'Parsing…' : 'Import XLSX'}
        </button>
        <input
          ref={importFileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          onChange={handleImportChange}
        />
        <button
          type="button"
          className="forecast-secondary-button compact"
          disabled={!editable || !canUndo}
          onClick={undoLastEdit}
        >
          <Undo2 size={12} aria-hidden="true" /> Undo last edit
        </button>
        <span className={`forecast-health-chip ${validation.status === 'blocked' ? 'red' : 'green'} mini`}>
          {validation.status === 'blocked'
            ? `${validation.errors.length} spread issue${validation.errors.length === 1 ? '' : 's'}`
            : 'Spread checks pass'}
        </span>
      </div>

      {validation.status === 'blocked' && (
        <div className="forecast-panel forecast-home-error" role="alert" style={{ marginBottom: 12 }}>
          <strong>Fix spread validation before Regenerate or Pro Forma.</strong>
          <ul>
            {validation.errors.slice(0, 6).map((issue) => (
              <li key={`${issue.collection}-${issue.lineId}-${issue.field}`}>
                {issue.lineDescription || issue.lineId}: {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {importPreview && (
        <div
          className={importPreview.validation?.status === 'blocked' ? 'forecast-panel forecast-home-error' : 'forecast-panel forecast-submit-note'}
          role="status"
          style={{ marginBottom: 12 }}
        >
          <strong>Import preview: {importPreview.diffSummary?.changedLineCount || 0} line(s) would change.</strong>
          <p>
            {importPreview.validation?.status === 'blocked'
              ? 'Resolve workbook validation errors before apply.'
              : 'Review the parsed changes, then apply to update the project spread and recompute server financials.'}
          </p>
          {(importPreview.validation?.errors || []).length > 0 && (
            <ul>
              {importPreview.validation.errors.slice(0, 6).map((issue) => (
                <li key={`${issue.collection}-${issue.lineId}-${issue.field}`}>{issue.lineDescription || issue.lineId}: {issue.message}</li>
              ))}
            </ul>
          )}
          <div className="workspace-action-row">
            <button
              type="button"
              className="forecast-primary-button"
              disabled={importPreview.validation?.status === 'blocked' || spreadBusy === 'spread-import-apply'}
              onClick={() => onApplyImport?.(importPreview.importId)}
            >
              {spreadBusy === 'spread-import-apply' ? 'Applying…' : 'Apply import'}
            </button>
            <button type="button" className="forecast-secondary-button compact" onClick={onCancelImport}>
              Cancel preview
            </button>
          </div>
        </div>
      )}

      {(template.assumptions || []).length > 0 && (
        <div className="forecast-submit-chips project-assumption-list" style={{ marginBottom: 10 }}>
          {template.assumptions.map((assumption, index) => (
            <span className="forecast-health-chip neutral" key={index}>{assumption}</span>
          ))}
        </div>
      )}

      <div className="workspace-stage-tabs" role="tablist" aria-label="Project spread categories">
        {[
          ['revenue', `Revenue (${(template.revenueLines || []).length})`],
          ['opex', `Operating Expense (${(template.opexLines || []).length})`],
          ['capex', `Capitalized Expense (${(template.capexItems || []).length})`]
        ].map(([id, label]) => (
          <button
            type="button"
            role="tab"
            aria-selected={spreadTab === id}
            aria-controls={`project-spread-panel-${id}`}
            id={`project-spread-tab-${id}`}
            tabIndex={spreadTab === id ? 0 : -1}
            className="forecast-secondary-button compact"
            onClick={() => setSpreadTab(id)}
            key={id}
          >
            {label}
          </button>
        ))}
      </div>

      {spreadTab === 'revenue' && (
        <div role="tabpanel" id="project-spread-panel-revenue" aria-labelledby="project-spread-tab-revenue">
          <h4 className="project-block-title">Revenue lines ($MM/yr) — non-marginal lines stay visible but excluded</h4>
          <div className="forecast-home-table-wrap" role="region" aria-label="Revenue spread table" tabIndex="0">
            <table className="forecast-home-table op3-year-table aligned" data-project-grid onKeyDownCapture={moveGridFocus}>
              <thead>
                <tr>
                  <th>Line</th><th>Marginal?</th>
                  {YEARS.map((year) => <th key={year}>FY{year}</th>)}
                  <th />
                </tr>
              </thead>
              <tbody>
                {(template.revenueLines || []).map((line, lineIndex) => (
                  <tr
                    key={line.id}
                    id={`project-spread-line-${line.id}`}
                    tabIndex={focusedLineId === line.id ? -1 : undefined}
                    className={line.marginal ? '' : 'project-excluded'}
                    style={focusedLineId === line.id ? { outline: '2px solid var(--ace-gold, #FFAC03)', outlineOffset: '-2px' } : undefined}
                  >
                    <td>
                      <strong>{line.description}</strong> <ProposalStatus line={line} /> {citationChip(line.sourceFigureId)}
                      {!line.marginal && (editable ? (
                        <input
                          type="text"
                          name={`revenue-exclusion-${line.id}`}
                          autoComplete="off"
                          aria-label={`${line.description} exclusion reason`}
                          className="op3-note wide"
                          value={line.exclusionReason || ''}
                          placeholder="Why is this revenue existing or non-marginal?…"
                          onChange={(event) => updateLine('revenueLines', line.id, { exclusionReason: event.target.value })}
                        />
                      ) : <small className="forecast-home-market"> {line.exclusionReason || 'Excluded'}</small>)}
                    </td>
                    <td>
                      <select
                        aria-label={`${line.description} revenue classification`}
                        value={line.marginal ? 'yes' : 'no'}
                        disabled={!editable}
                        onChange={(event) => updateLine('revenueLines', line.id, {
                          marginal: event.target.value === 'yes',
                          byYear: event.target.value === 'yes' ? line.byYear : [0, 0, 0, 0, 0]
                        })}
                      >
                        <option value="yes">Marginal</option>
                        <option value="no">Existing</option>
                      </select>
                    </td>
                    {YEARS.map((year, yearIndex) => {
                      const error = cellError('revenueLines', line.id, `byYear.${yearIndex}`);
                      return (
                      <td key={year} data-grid-cell data-grid-row={lineIndex} data-grid-col={yearIndex} style={errorStyle(error)}>
                        <NumericInput
                          ariaLabel={`${line.description} revenue FY${year}`}
                          value={line.byYear[yearIndex]}
                          step={0.1}
                          className={inputClass(error)}
                          title={error?.message}
                          disabled={!editable || !line.marginal}
                          onCommit={(value) => updateLine('revenueLines', line.id, {
                            byYear: line.byYear.map((item, index) => (index === yearIndex ? value : item))
                          })}
                        />
                        {errorMessage(error)}
                      </td>
                      );
                    })}
                    <td>
                      {editable && (
                        <button
                          type="button"
                          className="hire-expand danger"
                          aria-label={`Remove revenue line ${line.description}`}
                          onClick={() => removeLine('revenueLines', line.id)}
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <TotalsFooter
                sectionLabel="Revenue section total"
                sectionValues={totals.revenueByYear}
                sectionTotal={totals.revenueTotal}
                fixedSpan={2}
              />
            </table>
          </div>
          {editable && (
            <button
              type="button"
              className="forecast-secondary-button compact"
              onClick={() => addLine('revenueLines', {
                id: newId(),
                description: 'New marginal revenue line',
                marginal: true,
                exclusionReason: '',
                byYear: [0, 0, 0, 0, 0],
                proposalStatus: 'manual'
              })}
            >
              <Plus size={12} aria-hidden="true" /> Revenue line
            </button>
          )}
        </div>
      )}

      {spreadTab === 'opex' && (
        <div role="tabpanel" id="project-spread-panel-opex" aria-labelledby="project-spread-tab-opex">
          <h4 className="project-block-title">Operating expense lines ($MM/yr) — existing/reallocated stays excluded</h4>
          <div className="forecast-home-table-wrap" role="region" aria-label="Operating expense spread table" tabIndex="0">
            <table className="forecast-home-table op3-year-table aligned" data-project-grid onKeyDownCapture={moveGridFocus}>
              <thead>
                <tr>
                  <th>Line</th><th>Incremental?</th><th>Capitalization</th>
                  {YEARS.map((year) => <th key={year}>FY{year}</th>)}
                  <th />
                </tr>
              </thead>
              <tbody>
                {(template.opexLines || []).map((line, lineIndex) => (
                  <tr
                    key={line.id}
                    id={`project-spread-line-${line.id}`}
                    tabIndex={focusedLineId === line.id ? -1 : undefined}
                    className={line.existing ? 'project-excluded' : ''}
                    style={focusedLineId === line.id ? { outline: '2px solid var(--ace-gold, #FFAC03)', outlineOffset: '-2px' } : undefined}
                  >
                    <td>
                      <strong>{line.description}</strong> <ProposalStatus line={line} /> {citationChip(line.sourceFigureId)}
                      {line.existing && (editable ? (
                        <input
                          type="text"
                          name={`opex-exclusion-${line.id}`}
                          autoComplete="off"
                          aria-label={`${line.description} exclusion reason`}
                          className="op3-note wide"
                          value={line.exclusionReason || ''}
                          placeholder="Why is this cost existing, sunk, or reallocated?…"
                          onChange={(event) => updateLine('opexLines', line.id, { exclusionReason: event.target.value })}
                        />
                      ) : <small className="forecast-home-market"> {line.exclusionReason || 'Excluded'}</small>)}
                    </td>
                    <td>
                      <select
                        aria-label={`${line.description} operating expense classification`}
                        value={line.existing ? 'no' : 'yes'}
                        disabled={!editable}
                        onChange={(event) => updateLine('opexLines', line.id, {
                          existing: event.target.value === 'no',
                          byYear: event.target.value === 'yes' ? line.byYear : [0, 0, 0, 0, 0]
                        })}
                      >
                        <option value="yes">Incremental</option>
                        <option value="no">Existing / sunk</option>
                      </select>
                    </td>
                    <CapitalizationCell
                      collection="opexLines"
                      line={line}
                      locked={Boolean(line.existing)}
                      editable={editable}
                      defaultLabel={line.existing ? 'Excluded · sunk' : 'Period expense'}
                      overrideLabel={`Override: capitalized · ${bucketTermMonths(line.capOverride?.bucket || 'other_capital')}mo`}
                      toggleOverride={{ mode: 'capitalize', bucket: 'other_capital', inServiceMonth: 12 }}
                      toggleTitle="Override: capitalize this line (other capital, 60mo straight-line, in service FY2027 M12)"
                      clearCapOverride={clearCapOverride}
                      setCapOverride={setCapOverride}
                    />
                    {YEARS.map((year, yearIndex) => {
                      const error = cellError('opexLines', line.id, `byYear.${yearIndex}`);
                      return (
                      <td key={year} data-grid-cell data-grid-row={lineIndex} data-grid-col={yearIndex} style={errorStyle(error)}>
                        <NumericInput
                          ariaLabel={`${line.description} operating expense FY${year}`}
                          value={line.byYear[yearIndex]}
                          step={0.05}
                          className={inputClass(error)}
                          title={error?.message}
                          disabled={!editable || line.existing}
                          onCommit={(value) => updateLine('opexLines', line.id, {
                            byYear: line.byYear.map((item, index) => (index === yearIndex ? value : item))
                          })}
                        />
                        {errorMessage(error)}
                      </td>
                      );
                    })}
                    <td>
                      {editable && (
                        <button
                          type="button"
                          className="hire-expand danger"
                          aria-label={`Remove operating expense line ${line.description}`}
                          onClick={() => removeLine('opexLines', line.id)}
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <TotalsFooter
                sectionLabel="Operating expense section total"
                sectionValues={totals.opexByYear}
                sectionTotal={totals.opexTotal}
                fixedSpan={3}
              />
            </table>
          </div>
          {editable && (
            <button
              type="button"
              className="forecast-secondary-button compact"
              onClick={() => addLine('opexLines', {
                id: newId(),
                description: 'New operating expense',
                existing: false,
                exclusionReason: '',
                byYear: [0, 0, 0, 0, 0],
                proposalStatus: 'manual'
              })}
            >
              <Plus size={12} aria-hidden="true" /> Opex line
            </button>
          )}
        </div>
      )}

      {spreadTab === 'capex' && (
        <div role="tabpanel" id="project-spread-panel-capex" aria-labelledby="project-spread-tab-capex">
          <h4 className="project-block-title">Capitalized items — the bucket dictates the term; spend is cash timing</h4>
          <div className="forecast-home-table-wrap" role="region" aria-label="Capitalized expense spread table" tabIndex="0">
            <table className="forecast-home-table op3-year-table aligned" data-project-grid onKeyDownCapture={moveGridFocus}>
              <thead>
                <tr>
                  <th>Item</th><th>Bucket (term)</th><th>Capitalization</th><th>Total $MM</th><th>In service</th>
                  {YEARS.map((year) => <th key={year}>FY{year} spend</th>)}
                  <th />
                </tr>
              </thead>
              <tbody>
                {(template.capexItems || []).map((item, lineIndex) => {
                  const totalError = cellError('capexItems', item.id, 'totalCostMM') || cellError('capexItems', item.id, 'spendByYear');
                  const inServiceError = cellError('capexItems', item.id, 'inServiceMonth');
                  return (
                  <tr
                    key={item.id}
                    id={`project-spread-line-${item.id}`}
                    tabIndex={focusedLineId === item.id ? -1 : undefined}
                    style={focusedLineId === item.id ? { outline: '2px solid var(--ace-gold, #FFAC03)', outlineOffset: '-2px' } : undefined}
                  >
                    <td>
                      <strong>{item.description}</strong> <ProposalStatus line={item} /> {citationChip(item.sourceFigureId)}
                      {item.rationale && <small className="forecast-home-market"> {item.rationale}</small>}
                    </td>
                    <td>
                      <select
                        aria-label={`${item.description} capitalization bucket`}
                        value={item.bucket}
                        disabled={!editable || item.capOverride?.mode === 'expense'}
                        onChange={(event) => updateLine('capexItems', item.id, { bucket: event.target.value })}
                      >
                        {capexBuckets.map((bucket) => (
                          <option key={bucket.id} value={bucket.id}>
                            {bucket.label} · {bucket.termMonths / 12}y
                          </option>
                        ))}
                      </select>
                    </td>
                    <CapitalizationCell
                      collection="capexItems"
                      line={item}
                      editable={editable}
                      defaultLabel={`Capitalized · ${bucketTermMonths(item.bucket)}mo`}
                      overrideLabel="Override: expensed"
                      toggleOverride={{ mode: 'expense' }}
                      toggleTitle="Override: treat as period expense (spend flows to opex; no depreciation or NBV)"
                      clearCapOverride={clearCapOverride}
                      setCapOverride={setCapOverride}
                    />
                    <td data-grid-cell data-grid-row={lineIndex} data-grid-col={0} style={errorStyle(totalError)}>
                      <NumericInput
                        ariaLabel={`${item.description} total cost`}
                        value={item.totalCostMM}
                        step={0.1}
                        className={inputClass(totalError)}
                        title={totalError?.message}
                        disabled={!editable || item.capOverride?.mode === 'expense'}
                        onCommit={(value) => updateLine('capexItems', item.id, { totalCostMM: value })}
                      />
                      {errorMessage(totalError)}
                    </td>
                    <td style={errorStyle(inServiceError)}>
                      <select
                        aria-label={`${item.description} in-service month`}
                        value={item.inServiceMonth}
                        disabled={!editable || item.capOverride?.mode === 'expense'}
                        onChange={(event) => updateLine('capexItems', item.id, { inServiceMonth: Number(event.target.value) })}
                      >
                        {Array.from({ length: 36 }, (_, month) => (
                          <option key={month + 1} value={month + 1}>
                            FY{YEARS[Math.floor(month / 12)]} M{String((month % 12) + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      {errorMessage(inServiceError)}
                    </td>
                    {YEARS.map((year, yearIndex) => {
                      const error = cellError('capexItems', item.id, `spendByYear.${yearIndex}`)
                        || cellError('capexItems', item.id, 'spendByYear');
                      return (
                      <td key={year} data-grid-cell data-grid-row={lineIndex} data-grid-col={yearIndex + 1} style={errorStyle(error)}>
                        <NumericInput
                          ariaLabel={`${item.description} spend FY${year}`}
                          value={item.spendByYear[yearIndex]}
                          step={0.1}
                          className={inputClass(error)}
                          title={error?.message}
                          disabled={!editable}
                          onCommit={(value) => updateLine('capexItems', item.id, {
                            spendByYear: item.spendByYear.map((amount, index) => (index === yearIndex ? value : amount))
                          })}
                        />
                        {errorMessage(error)}
                      </td>
                      );
                    })}
                    <td>
                      {editable && (
                        <button
                          type="button"
                          className="hire-expand danger"
                          aria-label={`Remove capitalized expense ${item.description}`}
                          onClick={() => removeLine('capexItems', item.id)}
                        >
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <TotalsFooter
                sectionLabel="Capex section total"
                sectionValues={totals.capexSpendByYear}
                sectionTotal={totals.capexSpendTotal}
                fixedSpan={3}
                includeCostTotal
              />
            </table>
          </div>
          {editable && (
            <button
              type="button"
              className="forecast-secondary-button compact"
              onClick={() => addLine('capexItems', {
                id: newId(),
                description: 'New capital item',
                bucket: 'other_capital',
                totalCostMM: 0,
                spendByYear: [0, 0, 0, 0, 0],
                inServiceMonth: 12,
                rationale: '',
                proposalStatus: 'manual'
              })}
            >
              <Plus size={12} aria-hidden="true" /> Capex item
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default CorporateProjectSpreadStage;
